import { match } from 'ts-pattern'
import {
  type SerializedCompStructure,
  type StackItemLayout,
  type Margins,
  type FlexDirection,
} from '@wix/document-services-types'
import { entries } from '@wix/wix-data-client-common-standalone'

import { assertNonNullable } from './assertNonNullable'
import { assertStackContainerLayout } from './assertStackContainerLayout'
import { assertStackItemLayout } from './assertStackItemLayout'

const normalizeLastStackItemMargins = ({
  margins,
  stackDirection,
}: {
  margins: Margins
  stackDirection: FlexDirection
}): Margins => {
  const marginKey = match(stackDirection)
    .with('column', () => 'bottom' as const)
    .with('row', () => 'right' as const)
    .otherwise(() => {
      throw new Error(`Unexpected stack direction "${stackDirection}".`)
    })

  const margin = margins[marginKey]
  return margin === undefined ||
    ('value' in margin &&
      typeof margin.value === 'number' &&
      margin.value === 0)
    ? margins
    : {
        ...margins,
        [marginKey]: {
          ...('value' in margin && typeof margin.value === 'number'
            ? { ...margin }
            : { type: 'percentage' }),
          value: 0,
        },
      }
}

const normalizeMarginOfLastStackItemLayout = ({
  stackItemLayout,
  stackDirection,
}: {
  stackItemLayout: StackItemLayout
  stackDirection: FlexDirection
}): StackItemLayout =>
  // `margins` is not in `StackItemLayout` but it is in real data.
  'margins' in stackItemLayout && stackItemLayout.margins !== undefined
    ? {
        ...stackItemLayout,
        ...{
          margins: normalizeLastStackItemMargins({
            margins: stackItemLayout.margins as Margins,
            stackDirection,
          }),
        },
      }
    : stackItemLayout

const normalizeMarginOfLastStackItem = ({
  stackItem,
  stackDirection,
  stackDirectionByBreakpointId,
}: {
  stackItem: SerializedCompStructure
  stackDirection: FlexDirection
  stackDirectionByBreakpointId: { [BreakpoinId: string]: FlexDirection }
}): SerializedCompStructure => {
  assertNonNullable(stackItem.layouts?.itemLayout)
  assertStackItemLayout(stackItem.layouts.itemLayout)

  return {
    ...stackItem,
    layouts: {
      ...stackItem.layouts,
      itemLayout: normalizeMarginOfLastStackItemLayout({
        stackItemLayout: stackItem.layouts.itemLayout,
        stackDirection,
      }),
    },
    ...(stackItem.scopedLayouts !== undefined
      ? {
          scopedLayouts: Object.fromEntries(
            entries(stackItem.scopedLayouts).map(
              ([breakpointId, scopedLayout]) => [
                breakpointId,
                scopedLayout.itemLayout !== undefined &&
                'type' in scopedLayout.itemLayout &&
                scopedLayout.itemLayout.type === 'StackItemLayout'
                  ? {
                      ...scopedLayout,
                      itemLayout: normalizeMarginOfLastStackItemLayout({
                        stackItemLayout: scopedLayout.itemLayout,
                        stackDirection:
                          stackDirectionByBreakpointId[breakpointId] ??
                          stackDirection,
                      }),
                    }
                  : scopedLayout,
              ],
            ),
          ),
        }
      : null),
  }
}

export const normalizeLastItemMarginInStack = (
  stack: SerializedCompStructure,
): SerializedCompStructure => {
  assertNonNullable(stack.components)
  if (stack.components.length === 0) {
    return stack
  }

  assertNonNullable(stack.layouts?.containerLayout)
  assertStackContainerLayout(stack.layouts.containerLayout)
  assertNonNullable(stack.layouts.containerLayout.direction)

  return {
    ...stack,
    components: [
      ...stack.components.slice(0, stack.components.length - 1),
      normalizeMarginOfLastStackItem({
        stackItem: stack.components[stack.components.length - 1],
        stackDirection: stack.layouts.containerLayout.direction,
        stackDirectionByBreakpointId:
          stack.scopedLayouts !== undefined
            ? entries(stack.scopedLayouts).reduce<{
                [BreakpoinId: string]: FlexDirection
              }>(
                (stackDirectionByBreakpointId, [breakpoinId, scropedLayout]) =>
                  scropedLayout.containerLayout !== undefined &&
                  'direction' in scropedLayout.containerLayout &&
                  scropedLayout.containerLayout.direction !== undefined
                    ? {
                        ...stackDirectionByBreakpointId,
                        [breakpoinId]: scropedLayout.containerLayout.direction,
                      }
                    : stackDirectionByBreakpointId,
                {},
              )
            : {},
      }),
    ],
  }
}
